<script>
import Multiselect from 'vue-multiselect'

export default {
  extends: Multiselect,
  props: {
    allowEmpty: { type: Boolean, default: false },
    selectLabel: { type: String, default: '' },
    selectedLabel: { type: String, default: '' },
    deselectLabel: { type: String, default: '' }
  },
  created() {
    this.$on('open', this.ensureDropdownSpace)
    this.$on('open', this.attachViewportListener)
    this.$on('close', this.detachViewportListener)
  },
  beforeDestroy() {
    this.detachViewportListener()
  },
  methods: {
    // window.innerHeight is the layout viewport - on mobile it commonly stays the same size
    // when the on-screen keyboard opens, even though the keyboard covers a large chunk of the
    // actual visible area. window.visualViewport.height tracks what's really visible (shrinks
    // when the keyboard is up), so space calculations need it to avoid sizing/positioning the
    // dropdown against space the keyboard has already covered.
    viewportHeight() {
      return (window.visualViewport && window.visualViewport.height) || window.innerHeight
    },
    // Re-run while the dropdown is open in case the keyboard opens/closes after activate() -
    // e.g. the field was focused programmatically (no keyboard yet), then the user taps in to
    // type and the keyboard appears, shrinking the space the original calculation assumed.
    // Also watch document.body's size: activate() positions the dropdown against the field's
    // rect at open time, but content elsewhere on the page (e.g. a sibling row's competitor
    // name that resolves once the members list finishes loading from the store) can still grow
    // after that and push this field further down - reproduced with a field whose row shifted
    // ~50px between open and the async label arriving, leaving the already-positioned dropdown
    // sized against space that no longer exists. A body resize (from that reflow, from anywhere
    // on the page) is the general signal to recheck, not just this field's own rect.
    attachViewportListener() {
      if (window.visualViewport) {
        this._onViewportResize = () => this.adjustPosition()
        window.visualViewport.addEventListener('resize', this._onViewportResize)
      }
      if (typeof ResizeObserver !== 'undefined') {
        this._bodyResizeObserver = new ResizeObserver(() => this.adjustPosition())
        this._bodyResizeObserver.observe(document.body)
      }
    },
    detachViewportListener() {
      if (window.visualViewport && this._onViewportResize) {
        window.visualViewport.removeEventListener('resize', this._onViewportResize)
      }
      this._onViewportResize = null
      if (this._bodyResizeObserver) {
        this._bodyResizeObserver.disconnect()
        this._bodyResizeObserver = null
      }
    },
    // EXPERIMENT (not a final decision - for visual review): pick the side with more room as
    // before, but don't shrink optimizedHeight to fit whatever's actually there. Always use
    // the full maxHeight, letting the dropdown overlap neighbouring content (or even the
    // screen edge) instead of clamping down to a cramped box. Original clamped version is in
    // git history (this file, previous commit) if this doesn't look right.
    adjustPosition() {
      if (typeof window === 'undefined') return
      const viewportHeight = this.viewportHeight()
      const spaceAbove = this.$el.getBoundingClientRect().top
      const spaceBelow = viewportHeight - this.$el.getBoundingClientRect().bottom
      const hasEnoughSpaceBelow = spaceBelow > this.maxHeight

      if (hasEnoughSpaceBelow || spaceBelow > spaceAbove || this.openDirection === 'below' || this.openDirection === 'bottom') {
        this.prefferedOpenDirection = 'below'
        this.optimizedHeight = this.maxHeight
      } else {
        this.prefferedOpenDirection = 'above'
        this.optimizedHeight = this.maxHeight
      }
    },
    // Override of multiselectMixin's activate(): identical except the final `else` branch is
    // guarded. That branch runs when the root div is not searchable, and it's reached via the
    // div's own `@focus="activate()"` binding - so this.$el is already document.activeElement
    // at this point. Calling this.$el.focus() on an element from inside its own native `focus`
    // handler is a re-entrant focus() call, and Chromium answers it by firing a fresh
    // blur+focus pair on the same element instead of treating it as a no-op. HorseSearchSelect
    // reacts to that blur (onFocusOut -> active = false) and to the refocus (onFocusIn), which
    // calls activate() again - landing right back on this same branch forever. Reproduced with
    // a hooked focus()/blur(): EventAddDetails.vue's many-horse-select page pinned one field's
    // input at 250+ focus/blur cycles/sec with no user interaction. Skipping the call when
    // already focused removes the re-entrant trigger; every other activate() caller (keyboard,
    // a genuinely different focused element) is unaffected since the condition is only false
    // when the redundant self-focus is what would have happened.
    activate() {
      if (this.isOpen || this.disabled) return
      this.adjustPosition()
      if (this.groupValues && this.pointer === 0 && this.filteredOptions.length) {
        this.pointer = 1
      }
      this.isOpen = true
      if (this.searchable) {
        if (!this.preserveSearch) this.search = ''
        this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
      } else if (document.activeElement !== this.$el) {
        this.$el.focus()
      }
      this.$emit('open', this.id)
    },
    // vue-multiselect sizes the dropdown to whatever viewport space is left above/below the
    // field at open time (adjustPosition() in multiselectMixin.js), so the same dropdown can
    // render at full height on one page and shrink down to a single option elsewhere. If
    // neither direction currently has room for a full-height dropdown, scroll the field into
    // view first so the height calculation has something reasonable to work with.
    ensureDropdownSpace() {
      const rect = this.$el.getBoundingClientRect()
      const hasSpaceBelow = this.viewportHeight() - rect.bottom >= this.maxHeight
      const hasSpaceAbove = rect.top >= this.maxHeight

      if (!hasSpaceBelow && !hasSpaceAbove) {
        this.$el.scrollIntoView({ block: 'center', behavior: 'instant' })
      }

      this.adjustPosition()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/AppMultiselect.vue